import React, { MutableRefObject, useEffect, useRef } from 'react'

import * as S from './styles'
import { Image } from '../image'

export type ImageAnimationProps = {
  imagesUrl: string[]
}

const { animationClasses } = S
// Distancia que o usuario precisa mover o mouse para renderizar uma nova imagem
const desktopOffset = 200
const mobileOffset = 80
const widthThresholdForMobile = 800

export const ImageAnimation: React.FC<ImageAnimationProps> = ({
  imagesUrl,
}) => {
  if (imagesUrl.length === 0) {
    throw new Error(
      'You need provide a initial array with values to render this component',
    )
  }

  const stateRef = useRef({
    lastImageIndex: 0,
    lastRenderPosition: { x: 0, y: 0 },
    images: new Array(imagesUrl.length)
      .fill(0)
      .map(() => React.createRef() as MutableRefObject<HTMLDivElement>),
  })

  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleAnimation = (x: number, y: number, movementX?: number) => {
      const state = stateRef.current

      if (!state) return

      const offsetToRenderNewImage =
        window.innerWidth < widthThresholdForMobile
          ? mobileOffset
          : desktopOffset

      const diffOnXAxis = state.lastRenderPosition.x - x
      const diffOnYAxis = state.lastRenderPosition.y - y
      const movementExceedOffset =
        Math.abs(diffOnXAxis) > offsetToRenderNewImage ||
        Math.abs(diffOnYAxis) > offsetToRenderNewImage
      if (!movementExceedOffset) return

      // Para nao renderizar imagens quando o mouse estiver bem perto do fim da tela
      const isOffsideScreen =
        y + state.images[0].current.offsetHeight >=
        (containerRef.current?.clientHeight || 0)
      if (isOffsideScreen) return

      const [lastHiddenImage] = state.images.filter(
        (i) =>
          !i.current.classList.contains(animationClasses.left) &&
          !i.current.classList.contains(animationClasses.right) &&
          !i.current.classList.contains(animationClasses.center),
      )

      if (!lastHiddenImage) return

      let animationClass = animationClasses.center
      if (movementX) {
        const movingToLeft = movementX < 0
        animationClass = movingToLeft
          ? animationClasses.left
          : animationClasses.right
      }

      lastHiddenImage.current.classList.add(animationClass)

      const imgWidth = lastHiddenImage.current.clientWidth

      lastHiddenImage.current.style.top = `${y - imgWidth / 2}px`
      lastHiddenImage.current.style.left = `${x - imgWidth / 2}px`

      state.lastRenderPosition = { x, y }
    }

    const handleMouseMove = (e: MouseEvent) => {
      handleAnimation(e.offsetX, e.offsetY, e.movementX)
    }
    const handleTouchMove = (e: TouchEvent) => {
      handleAnimation(e.targetTouches[0].pageX, e.targetTouches[0].pageY)
    }

    const newContainerRef = containerRef.current

    newContainerRef?.addEventListener('mousemove', handleMouseMove, {
      passive: true,
    })
    newContainerRef?.addEventListener('touchmove', handleTouchMove, {
      passive: true,
    })
    return () => {
      newContainerRef?.removeEventListener('mousemove', handleMouseMove)
      newContainerRef?.removeEventListener('touchmove', handleTouchMove)
    }
  }, [imagesUrl])

  useEffect(() => {
    const handleAnimationEnd = (e: AnimationEvent) => {
      const target = e.target as HTMLDivElement
      if (!target) return
      target.classList.remove(animationClasses.left)
      target.classList.remove(animationClasses.right)
      target.classList.remove(animationClasses.center)
    }

    const newStateRef = stateRef.current
    newStateRef.images.forEach((i) => {
      i.current.addEventListener('animationend', handleAnimationEnd)
    })

    return () => {
      newStateRef.images?.forEach((i) => {
        i.current?.removeEventListener('animationend', handleAnimationEnd)
      })
    }
  }, [])

  return (
    <S.Container ref={containerRef}>
      {stateRef.current.images.map((ref, i) => (
        <div key={imagesUrl[i]} ref={ref}>
          <Image src={imagesUrl[i]} alt="heroImg" fill priority />
        </div>
      ))}
    </S.Container>
  )
}
